import {useAddNotification} from '@logistics-frontend/blocks'
import {type Optional} from '@logistics-frontend/types'
import {allPass, equals, filter, flow, get, keys, pipe} from '@logistics-frontend/utils'
import {useFormContext, useFormState} from '@yandex-delivery/form'
import React, {useMemo} from 'react'

import {appConfig, i18n} from 'src/shared/config'
import {OrderEventGoal, useFormUserEventHandler} from 'src/shared/hooks'
import type {Order} from 'src/shared/types'
import {formatOfferInterval, formatPrice} from 'src/shared/utils'

import {useScrollToErrorField} from '../hooks/use-scroll-to-error-field'
import {fetchCreateOrder} from '../service/create-order'

export function useSubmitButtonTitle() {
  const {isSubmitting} = useFormState<Order>()

  if (isSubmitting) {
    return i18n('cargo.create.form.ordering', 'Оформляем...')
  }

  return i18n('cargo.create.form.order', 'Заказать')
}

export function useSubmitButtonDetails() {
  const {watch} = useFormContext<Order>()

  const selectedOffer = watch('offerFlow.offer')

  if (selectedOffer) {
    return {
      ETA: formatOfferInterval(selectedOffer.tariff, selectedOffer.visitingIntervals).short,
      price: formatPrice(selectedOffer.price, {
        currency: appConfig.currency,
        country: appConfig.countryCode,
        currencySign: appConfig.currencySign,
      }),
    }
  }

  return {}
}

/**
 * Причина блокировки кнопки "Заказать" из-за ошибок в заполнении формы
 */
function useFormInvalidDisableReason() {
  const {isValid} = useFormState<Order>()
  const isBusiness = useFormContext<Order>().watch('isBusiness')

  if (isValid) {
    return isBusiness
      ? i18n('form.new-tariff-line.submit-button.sub-title', 'После этого откроется личный кабинет для бизнеса')
      : undefined
  }

  return i18n('cargo.create.fill-requirement-fields', 'Заполните обязательные поля')
}

/**
 * Причина блокировки кнопки "Заказать" из-за отсутствия предложений
 */
function useFormEmptyOffersDisabledReason() {
  const {watch, errors, isValid} = useFormContext<Order>()

  const selectedVertical = watch('offerFlow.vertical.name')
  const offers = watch('__settings.__offers')
  const isBusiness = watch('isBusiness')
  const isOnlyOffersErrorLeft = pipe(
    errors,
    allPass([flow(get('offerFlow.offer.id.message'), Boolean), flow(keys, get('length'), equals(1))]),
  )

  const selectedVerticalOffersCount = pipe(
    offers,
    filter(flow(get('vertical'), equals(selectedVertical))),
    get('length'),
  )

  if (isValid) {
    return isBusiness
      ? i18n('form.new-tariff-line.submit-button.sub-title', 'После этого откроется личный кабинет для бизнеса')
      : undefined
  }

  if (isOnlyOffersErrorLeft && !!selectedVertical && selectedVerticalOffersCount === 0) {
    return i18n('form.new-tariff-line.disable-reason.empty-offers', 'Нет подходящих вариантов для ваших настроек')
  }

  return undefined
}

/**
 * Причина блокировки кнопки "Заказать"
 */
export function useDisableReason() {
  const invalidFormDisableReason = useFormInvalidDisableReason()
  const noOffersDisableReason = useFormEmptyOffersDisabledReason()

  return noOffersDisableReason || invalidFormDisableReason
}

/**
 * Причина блокировки кнопки "Заказать" в мобильной версии
 * На десктоп пока нет макета
 */
export function useDisableReasonMobile() {
  const {watch, errors, isValid} = useFormContext<Order>()

  const selectedVertical = watch('offerFlow.vertical.name')
  const offers = watch('__settings.__offers')
  const isOnlyOffersErrorLeft = pipe(
    errors,
    allPass([flow(get('offerFlow.offer.id.message'), Boolean), flow(keys, get('length'), equals(1))]),
  )

  const selectedVerticalOffersCount = pipe(
    offers,
    filter(flow(get('vertical'), equals(selectedVertical))),
    get('length'),
  )

  if (isOnlyOffersErrorLeft && !!selectedVertical && selectedVerticalOffersCount === 0) {
    return 'Нет подходящих вариантов доставки'
  }

  if (isValid) {
    return undefined
  }

  return 'Заполните данные'
}

export function useSubmitHandler(formRef: React.MutableRefObject<Optional<HTMLDivElement>>): VoidFunction {
  const addNotification = useAddNotification()
  const scrollToFirstError = useScrollToErrorField(formRef)
  const {handleSubmit} = useFormContext<Order>()
  const handleReachGoal = useFormUserEventHandler()

  return useMemo<VoidFunction>(
    function submitHandler() {
      function submitFormHandler(formValues: Order) {
        handleReachGoal(OrderEventGoal.SubmitButtonClick)
        const successSubmitHandler = handleReachGoal.bind(null, OrderEventGoal.SuccessOrderCreate)
        return fetchCreateOrder(formValues, successSubmitHandler).catch(addNotification)
      }

      function errorFormHandler() {
        requestAnimationFrame(scrollToFirstError)
      }

      return handleSubmit(submitFormHandler, errorFormHandler)
    },
    [handleSubmit, handleReachGoal, addNotification, scrollToFirstError],
  )
}
